import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const API_URL = '/api';

const authHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    };
};

const buildStatusLabel = (t) => ({
    pending: { text: t('profile.status.pending'), className: 'text-cyber-purple glow-purple' },
    verifying: { text: t('profile.status.verifying'), className: 'text-cyber-purple glow-purple' },
    processing: { text: t('profile.status.processing'), className: 'text-cyber-purple glow-purple' },
    completed: { text: t('profile.status.completed'), className: 'text-cyber-cyan glow-cyan' },
    rejected: { text: t('profile.status.rejected'), className: 'text-cyber-pink glow-pink' },
    failed: { text: t('profile.status.failed'), className: 'text-cyber-pink glow-pink' },
    cancelled: { text: t('profile.status.cancelled'), className: 'text-cyber-dim' },
});

const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleString();
};

const shortenWallet = (wallet) => {
    if (!wallet) return '—';
    if (wallet.length <= 14) return wallet;
    return `${wallet.slice(0, 6)}...${wallet.slice(-6)}`;
};

function Admin({ player, onLogout }) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const statusLabel = buildStatusLabel(t);

    const [tab, setTab] = useState('withdrawals');
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [withdrawals, setWithdrawals] = useState([]);
    const [filter, setFilter] = useState('verifying');
    const [actionId, setActionId] = useState(null);
    const [commentDraft, setCommentDraft] = useState({});
    const [codeDraft, setCodeDraft] = useState({});
    const [gameRounds, setGameRounds] = useState([]);
    const [roundsFilter, setRoundsFilter] = useState('all');
    const [roundsLoading, setRoundsLoading] = useState(false);

    const loadData = async () => {
        setLoading(true);
        try {
            const [wRes, uRes] = await Promise.all([
                fetch(`${API_URL}/admin/withdrawals`, { headers: authHeaders() }),
                fetch(`${API_URL}/admin/users`, { headers: authHeaders() }),
            ]);
            const wData = await wRes.json().catch(() => ({}));
            const uData = await uRes.json().catch(() => ({}));
            if (!wRes.ok) throw new Error(wData.message || t('admin.failLoadWithdrawals'));
            if (!uRes.ok) throw new Error(uData.message || t('admin.failLoadUsers'));
            setWithdrawals(wData.withdrawals || []);
            setUsers(uData.users || []);
        } catch (e) {
            toast.error(e.message);
            if (String(e.message).toLowerCase().includes('admin')) {
                navigate('/game');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!localStorage.getItem('token')) {
            navigate('/');
            return;
        }
        loadData();
        // eslint-disable-next-line
    }, []);

    const loadGameRounds = async (filterOverride) => {
        setRoundsLoading(true);
        try {
            const status = filterOverride ?? roundsFilter;
            const params = new URLSearchParams();
            if (status && status !== 'all') params.set('status', status);
            params.set('limit', '100');
            const res = await fetch(`${API_URL}/admin/game-rounds?${params.toString()}`, { headers: authHeaders() });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || t('admin.failLoadRounds'));
            setGameRounds(data.rows || []);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setRoundsLoading(false);
        }
    };

    useEffect(() => {
        if (tab === 'rounds') loadGameRounds();
        // eslint-disable-next-line
    }, [tab, roundsFilter]);

    const processWithdrawal = async (id, action) => {
        setActionId(`${action}-${id}`);
        try {
            const res = await fetch(`${API_URL}/admin/withdrawals/${id}/${action}`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ comment: commentDraft[id] || '' }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || t('admin.actionFailed'));
            const successKey = {
                approve: 'admin.approved',
                reject: 'admin.rejected',
                'force-fail': 'admin.forceFailed',
            }[action] || 'admin.actionDone';
            toast.success(t(successKey));
            setCommentDraft((prev) => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
            await loadData();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setActionId(null);
        }
    };

    const verifyWithdrawal = async (id) => {
        const code = (codeDraft[id] || '').trim();
        if (!code) {
            toast.error(t('admin.codeRequired'));
            return;
        }
        setActionId(`verify-${id}`);
        try {
            const res = await fetch(`${API_URL}/admin/withdrawals/${id}/verify`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ code }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.detail || data.message || t('admin.actionFailed'));
            toast.success(t('admin.verified'));
            setCodeDraft((prev) => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
            await loadData();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setActionId(null);
        }
    };

    const filteredWithdrawals =
        filter === 'all' ? withdrawals : withdrawals.filter((w) => w.status === filter);

    const verifyingCount = withdrawals.filter((w) => w.status === 'verifying').length;
    const pendingCount = withdrawals.filter(
        (w) => w.status === 'verifying' || w.status === 'pending' || w.status === 'processing',
    ).length;

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-cyber-dim tracking-widest text-xs animate-pulse">{t('admin.loadingPanel')}</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-2 sm:p-4 md:p-8">
            <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
                <header className="neon-panel scanlines p-3 sm:p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="text-xl sm:text-2xl font-black font-display text-white">
                            BG<span className="text-cyber-pink glow-pink ml-1">GAMES</span>
                            <span className="hidden sm:inline text-cyber-dim text-xs tracking-widest ml-3">{t('admin.console')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => navigate('/game')} className="neon-btn text-xs px-3 sm:px-4 py-1.5 sm:py-2">
                                {t('common.back')}
                            </button>
                            <button onClick={() => navigate('/profile')} className="neon-btn text-xs px-3 sm:px-4 py-1.5 sm:py-2">
                                {t('common.cabinet')}
                            </button>
                            {onLogout && (
                                <button onClick={onLogout} className="neon-btn neon-btn-pink text-xs px-3 sm:px-4 py-1.5 sm:py-2">
                                    {t('common.logout')}
                                </button>
                            )}
                        </div>
                    </div>
                </header>

                <div className="neon-panel p-2 sm:p-3 flex flex-wrap gap-2 sm:gap-3">
                    <button
                        onClick={() => setTab('withdrawals')}
                        className={`neon-btn text-xs px-3 sm:px-4 py-1.5 sm:py-2 ${tab === 'withdrawals' ? 'neon-btn-purple' : ''}`}
                    >
                        {t('admin.withdrawals')} {pendingCount > 0 && `[${pendingCount}]`}
                    </button>
                    <button
                        onClick={() => setTab('users')}
                        className={`neon-btn text-xs px-3 sm:px-4 py-1.5 sm:py-2 ${tab === 'users' ? 'neon-btn-purple' : ''}`}
                    >
                        {t('admin.users')} [{users.length}]
                    </button>
                    <button
                        onClick={() => setTab('rounds')}
                        className={`neon-btn text-xs px-3 sm:px-4 py-1.5 sm:py-2 ${tab === 'rounds' ? 'neon-btn-purple' : ''}`}
                    >
                        {t('admin.gameRounds')}
                    </button>
                    <button
                        onClick={tab === 'rounds' ? () => loadGameRounds() : loadData}
                        className="neon-btn text-xs px-3 sm:px-4 py-1.5 sm:py-2 ml-auto"
                    >
                        {t('common.refresh')}
                    </button>
                </div>

                {tab === 'withdrawals' && (
                    <section className="neon-panel p-3 sm:p-6">
                        <div className="flex items-center justify-between mb-4 sm:mb-5 flex-wrap gap-3">
                            <h2 className="text-base sm:text-xl font-display font-bold text-white glow-cyan">{t('admin.withdrawalRequests')}</h2>
                            <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                {['verifying', 'pending', 'processing', 'completed', 'failed', 'rejected', 'cancelled', 'all'].map((f) => (
                                    <button
                                        key={f}
                                        onClick={() => setFilter(f)}
                                        className={`neon-btn text-[10px] px-2 sm:px-3 py-1 ${filter === f ? 'neon-btn-purple' : ''}`}
                                    >
                                        {t(`admin.filter.${f}`)}
                                        {f === 'verifying' && verifyingCount > 0 && ` [${verifyingCount}]`}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {filteredWithdrawals.length === 0 ? (
                            <div className="text-center py-10 text-cyber-dim font-display tracking-widest text-sm">
                                {t('admin.noRequests')}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {filteredWithdrawals.map((w) => {
                                    const s = statusLabel[w.status] || statusLabel.pending;
                                    const showProcessed = !['pending', 'verifying'].includes(w.status);
                                    const isLegacyPending = w.status === 'pending';
                                    const isVerifying = w.status === 'verifying';
                                    const isProcessing = w.status === 'processing';
                                    return (
                                        <div key={w.id} className="border border-cyber-border/60 p-3 sm:p-4 rounded">
                                            <div className="grid grid-cols-2 sm:grid-cols-3 md:flex md:flex-wrap gap-3 sm:gap-5 text-sm">
                                                <div>
                                                    <div className="neon-label">{t('admin.id')}</div>
                                                    <div className="font-mono text-cyber-dim">#{w.id}</div>
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="neon-label">{t('admin.user')}</div>
                                                    <div className="font-display text-cyber-cyan glow-cyan truncate">
                                                        {w.user ? w.user.name : `#${w.userId}`}
                                                    </div>
                                                    {w.user && (
                                                        <div className="text-[10px] sm:text-xs text-cyber-dim font-mono truncate">{w.user.email}</div>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="neon-label">{t('admin.amount')}</div>
                                                    <div className="font-display text-cyber-purple glow-purple text-base sm:text-lg">
                                                        {w.amount}¢
                                                    </div>
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="neon-label">{t('admin.wallet')}</div>
                                                    <div className="font-mono text-cyber-text text-xs truncate" title={w.wallet}>
                                                        {shortenWallet(w.wallet)}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="neon-label">{t('admin.status')}</div>
                                                    <div className={`font-display tracking-wider ${s.className}`}>
                                                        {s.text}
                                                    </div>
                                                    {w.nowpaymentsStatus && (
                                                        <div className="text-[10px] text-cyber-dim font-mono">{w.nowpaymentsStatus}</div>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="neon-label">{t('admin.created')}</div>
                                                    <div className="font-mono text-cyber-dim text-[10px] sm:text-xs">
                                                        {formatDate(w.createdAt)}
                                                    </div>
                                                </div>
                                                {showProcessed && (
                                                    <div>
                                                        <div className="neon-label">{t('admin.processed')}</div>
                                                        <div className="font-mono text-cyber-dim text-[10px] sm:text-xs">
                                                            {formatDate(w.processedAt)}
                                                        </div>
                                                    </div>
                                                )}
                                                {w.nowpaymentsBatchId && (
                                                    <div className="min-w-0">
                                                        <div className="neon-label">{t('admin.batchId')}</div>
                                                        <div className="font-mono text-cyber-dim text-[10px] sm:text-xs truncate" title={w.nowpaymentsBatchId}>
                                                            {w.nowpaymentsBatchId}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {w.adminComment && (
                                                <div className="mt-3 text-xs">
                                                    <span className="neon-label mr-2">{t('admin.comment')}</span>
                                                    <span className="text-cyber-text font-mono break-all">{w.adminComment}</span>
                                                </div>
                                            )}
                                            {w.failureReason && (
                                                <div className="mt-2 text-xs">
                                                    <span className="neon-label mr-2 text-cyber-pink">{t('admin.failure')}</span>
                                                    <span className="text-cyber-pink font-mono break-all">{w.failureReason}</span>
                                                </div>
                                            )}

                                            {isVerifying && (
                                                <div className="mt-3 sm:mt-4 space-y-2">
                                                    <p className="text-[10px] text-cyber-dim tracking-widest">
                                                        {t('admin.verifyHint')}
                                                    </p>
                                                    <div className="flex flex-col md:flex-row gap-2 sm:gap-3">
                                                        <input
                                                            type="text"
                                                            inputMode="numeric"
                                                            maxLength={16}
                                                            value={codeDraft[w.id] || ''}
                                                            onChange={(e) =>
                                                                setCodeDraft((prev) => ({ ...prev, [w.id]: e.target.value }))
                                                            }
                                                            placeholder={t('admin.codePlaceholder')}
                                                            className="neon-input flex-1 w-full font-mono"
                                                        />
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => verifyWithdrawal(w.id)}
                                                                disabled={actionId !== null}
                                                                className="neon-btn neon-btn-purple text-xs px-3 sm:px-4 py-2 flex-1 md:flex-none"
                                                            >
                                                                {actionId === `verify-${w.id}` ? '...' : t('admin.verify')}
                                                            </button>
                                                            <button
                                                                onClick={() => processWithdrawal(w.id, 'force-fail')}
                                                                disabled={actionId !== null}
                                                                className="neon-btn neon-btn-pink text-xs px-3 sm:px-4 py-2 flex-1 md:flex-none"
                                                            >
                                                                {actionId === `force-fail-${w.id}` ? '...' : t('admin.forceFail')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {isProcessing && (
                                                <div className="mt-3 sm:mt-4 flex flex-col md:flex-row gap-2 sm:gap-3 items-start">
                                                    <p className="text-[10px] text-cyber-dim tracking-widest flex-1">
                                                        {t('admin.processingHint')}
                                                    </p>
                                                    <button
                                                        onClick={() => processWithdrawal(w.id, 'force-fail')}
                                                        disabled={actionId !== null}
                                                        className="neon-btn neon-btn-pink text-xs px-3 sm:px-4 py-2"
                                                    >
                                                        {actionId === `force-fail-${w.id}` ? '...' : t('admin.forceFail')}
                                                    </button>
                                                </div>
                                            )}

                                            {isLegacyPending && (
                                                <div className="mt-3 sm:mt-4 flex flex-col md:flex-row gap-2 sm:gap-3">
                                                    <input
                                                        type="text"
                                                        maxLength={512}
                                                        value={commentDraft[w.id] || ''}
                                                        onChange={(e) =>
                                                            setCommentDraft((prev) => ({ ...prev, [w.id]: e.target.value }))
                                                        }
                                                        placeholder={t('admin.commentPlaceholder')}
                                                        className="neon-input flex-1 w-full"
                                                    />
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => processWithdrawal(w.id, 'approve')}
                                                            disabled={actionId !== null}
                                                            className="neon-btn neon-btn-purple text-xs px-3 sm:px-4 py-2 flex-1 md:flex-none"
                                                        >
                                                            {actionId === `approve-${w.id}` ? '...' : t('admin.approve')}
                                                        </button>
                                                        <button
                                                            onClick={() => processWithdrawal(w.id, 'reject')}
                                                            disabled={actionId !== null}
                                                            className="neon-btn neon-btn-pink text-xs px-3 sm:px-4 py-2 flex-1 md:flex-none"
                                                        >
                                                            {actionId === `reject-${w.id}` ? '...' : t('admin.reject')}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                )}

                {tab === 'rounds' && (
                    <section className="neon-panel p-3 sm:p-6">
                        <div className="flex items-center justify-between mb-4 sm:mb-5 flex-wrap gap-3">
                            <h2 className="text-base sm:text-xl font-display font-bold text-white glow-cyan">{t('admin.gameRoundsTitle')}</h2>
                            <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                {['all', 'active', 'completed', 'cancelled'].map((f) => (
                                    <button
                                        key={f}
                                        onClick={() => setRoundsFilter(f)}
                                        className={`neon-btn text-[10px] px-2 sm:px-3 py-1 ${roundsFilter === f ? 'neon-btn-purple' : ''}`}
                                    >
                                        {t(`admin.roundsFilter.${f}`)}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {roundsLoading ? (
                            <div className="text-center py-10 text-cyber-dim font-display tracking-widest text-sm animate-pulse">
                                {t('common.loading')}
                            </div>
                        ) : gameRounds.length === 0 ? (
                            <div className="text-center py-10 text-cyber-dim font-display tracking-widest text-sm">
                                {t('admin.noRounds')}
                            </div>
                        ) : (
                            <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
                                <table className="w-full text-xs sm:text-sm min-w-[720px]">
                                    <thead>
                                        <tr className="text-left text-cyber-dim tracking-widest text-[10px] sm:text-xs uppercase">
                                            <th className="py-2 pr-2 sm:pr-4">{t('admin.roundId')}</th>
                                            <th className="py-2 pr-2 sm:pr-4">#</th>
                                            <th className="py-2 pr-2 sm:pr-4">{t('admin.gameType')}</th>
                                            <th className="py-2 pr-2 sm:pr-4">{t('admin.status')}</th>
                                            <th className="py-2 pr-2 sm:pr-4">{t('admin.playerA')}</th>
                                            <th className="py-2 pr-2 sm:pr-4">{t('admin.playerB')}</th>
                                            <th className="py-2 pr-2 sm:pr-4">{t('admin.amount')}</th>
                                            <th className="py-2 pr-2 sm:pr-4">{t('admin.winner')}</th>
                                            <th className="py-2 pr-2 sm:pr-4">{t('admin.created')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {gameRounds.map((r) => (
                                            <tr key={r.id} className="border-t border-cyber-border/40 hover:bg-cyber-border/10">
                                                <td className="py-2 pr-2 sm:pr-4 font-mono text-cyber-dim">
                                                    <Link to={`/verify/${r.id}`} className="text-cyan-400 hover:underline">
                                                        {r.id.substring(0, 8)}
                                                    </Link>
                                                </td>
                                                <td className="py-2 pr-2 sm:pr-4 font-mono text-cyber-dim">#{r.roundNumber}</td>
                                                <td className="py-2 pr-2 sm:pr-4 text-cyber-text">{r.gameType}</td>
                                                <td className="py-2 pr-2 sm:pr-4 font-display tracking-wider">{r.status}</td>
                                                <td className="py-2 pr-2 sm:pr-4 text-cyber-cyan glow-cyan">{r.playerA?.name || '—'}</td>
                                                <td className="py-2 pr-2 sm:pr-4 text-cyber-pink glow-pink">{r.playerB?.name || '—'}</td>
                                                <td className="py-2 pr-2 sm:pr-4 text-cyber-purple glow-purple">{r.betAmount}¢</td>
                                                <td className="py-2 pr-2 sm:pr-4 text-white">{r.isTie ? t('verify.tie') : (r.winner?.name || '—')}</td>
                                                <td className="py-2 pr-2 sm:pr-4 font-mono text-cyber-dim text-[10px] sm:text-xs">{formatDate(r.createdAt)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                )}

                {tab === 'users' && (
                    <section className="neon-panel p-3 sm:p-6">
                        <div className="flex items-center justify-between mb-4 sm:mb-5">
                            <h2 className="text-base sm:text-xl font-display font-bold text-white glow-cyan">{t('admin.usersTitle')}</h2>
                            <span className="text-xs text-cyber-dim tracking-widest">{t('admin.total', { count: users.length })}</span>
                        </div>
                        <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
                            <table className="w-full text-xs sm:text-sm min-w-[500px]">
                                <thead>
                                    <tr className="text-left text-cyber-dim tracking-widest text-[10px] sm:text-xs uppercase">
                                        <th className="py-2 pr-2 sm:pr-4">{t('admin.id')}</th>
                                        <th className="py-2 pr-2 sm:pr-4">{t('admin.name')}</th>
                                        <th className="py-2 pr-2 sm:pr-4">{t('admin.email')}</th>
                                        <th className="py-2 pr-2 sm:pr-4">{t('admin.balance')}</th>
                                        <th className="py-2 pr-2 sm:pr-4">{t('admin.wallet')}</th>
                                        <th className="py-2 pr-2 sm:pr-4">{t('admin.role')}</th>
                                        <th className="py-2 pr-2 sm:pr-4">{t('admin.joined')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((u) => (
                                        <tr key={u.id} className="border-t border-cyber-border/40">
                                            <td className="py-2 pr-2 sm:pr-4 font-mono text-cyber-dim">#{u.id}</td>
                                            <td className="py-2 pr-2 sm:pr-4 font-display text-cyber-cyan glow-cyan truncate max-w-[100px]">{u.name}</td>
                                            <td className="py-2 pr-2 sm:pr-4 font-mono text-cyber-text truncate max-w-[120px]">{u.email}</td>
                                            <td className="py-2 pr-2 sm:pr-4 font-display text-cyber-purple glow-purple">
                                                {u.balance}¢
                                            </td>
                                            <td className="py-2 pr-2 sm:pr-4 font-mono text-cyber-dim text-[10px] sm:text-xs" title={u.wallet || ''}>
                                                {shortenWallet(u.wallet)}
                                            </td>
                                            <td className="py-2 pr-2 sm:pr-4 font-display tracking-wider">
                                                {u.isAdmin ? (
                                                    <span className="text-cyber-pink glow-pink">{t('admin.roleAdmin')}</span>
                                                ) : (
                                                    <span className="text-cyber-dim">{t('admin.roleUser')}</span>
                                                )}
                                            </td>
                                            <td className="py-2 pr-2 sm:pr-4 font-mono text-cyber-dim text-[10px] sm:text-xs">
                                                {formatDate(u.createdAt)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}

export default Admin;
